import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Tooltip } from '@mui/material'
import styled from 'styled-components'
import { requestFileThumbnailsRoutine } from 'ducks/files/actions'
import { SkeletonBlock } from 'components/Skeleton'
import useFilePreview from 'features/bpDetails/Components/files/useFilePreview'

const PhotoSourcesContainer = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e8e8e8;
`

const PhotoSourcesLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const PhotoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`

const PhotoTile = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 96px;
  padding: 0;
  background: none;
  border: none;
  cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
  text-align: center;

  &:focus-visible {
    outline: none;
  }
`

const ThumbnailFrame = styled.span`
  display: block;
  width: 96px;
  height: 96px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f6f7;
  border: 1px solid #e8e8e8;

  ${PhotoTile}:hover & {
    border-color: #33becc;
  }

  ${PhotoTile}:focus-visible & {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }
`

const ThumbnailImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  -webkit-user-drag: none;
  user-select: none;
`

const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 11px;
  background: #f5f6f7;
`

const Caption = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey[200]};
  line-height: 1.3;
  width: 96px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const TooltipAnchor = styled.span`
  display: inline-flex;
`

const TooltipLine = styled.span`
  display: block;
  font-size: 14px;
  line-height: 1.4;

  & + & {
    margin-top: 4px;
    opacity: 0.85;
  }
`

const PhotoSourceTile = ({ citation, fallbackBpId }) => {
  const dispatch = useDispatch()
  const targetBpId = citation.buildingPassportId || fallbackBpId
  const thumbnailUrl = useSelector(state =>
    citation.id ? state.files.thumbnailsById[citation.id] : undefined
  )
  const canOpen = !!(citation.id && targetBpId)
  const { open: openPreview, previewingId, modal: previewModal } = useFilePreview(targetBpId)

  useEffect(() => {
    if (!citation.id || !targetBpId) return
    if (thumbnailUrl !== undefined) return
    dispatch(requestFileThumbnailsRoutine.trigger({ bpId: targetBpId, fileId: citation.id }))
  }, [citation.id, targetBpId, thumbnailUrl, dispatch])

  return (
    <>
      <Tooltip
        placement='top'
        arrow
        title={
          (citation.description || citation.caption || canOpen) ? (
            <>
              {(citation.description || citation.caption) && (
                <TooltipLine>{citation.description || citation.caption}</TooltipLine>
              )}
              {canOpen && <TooltipLine>Click or tap to see the full photo</TooltipLine>}
            </>
          ) : ''
        }
      >
        <TooltipAnchor>
          <PhotoTile
            type='button'
            disabled={!canOpen || previewingId === citation.id}
            onClick={() => canOpen && openPreview({
              id: citation.id,
              displayName: citation.name,
              name: citation.name,
              versions: citation.versionId ? [{ id: citation.versionId }] : []
            })}
            aria-label={`Open photo ${citation.name || 'file'}`}
          >
            <ThumbnailFrame>
              {thumbnailUrl ? (
                <ThumbnailImg
                  src={thumbnailUrl}
                  alt={citation.name || 'Photo'}
                  draggable={false}
                />
              ) : thumbnailUrl === undefined && targetBpId ? (
                <SkeletonBlock $h={96} $w={96} $r='8px' />
              ) : (
                <ThumbnailPlaceholder>No preview</ThumbnailPlaceholder>
              )}
            </ThumbnailFrame>
            {citation.caption && <Caption>{citation.caption}</Caption>}
          </PhotoTile>
        </TooltipAnchor>
      </Tooltip>
      {previewModal}
    </>
  )
}

const GuruPhotoSources = ({ citations, fallbackBpId }) => {
  if (!citations?.length) return null

  return (
    <PhotoSourcesContainer>
      <PhotoSourcesLabel>Photos</PhotoSourcesLabel>
      <PhotoRow>
        {citations.map((citation, i) => (
          <PhotoSourceTile
            key={citation.id || i}
            citation={citation}
            fallbackBpId={fallbackBpId}
          />
        ))}
      </PhotoRow>
    </PhotoSourcesContainer>
  )
}

export default GuruPhotoSources
